import React, { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import CheckboxToggle from '~/components/atoms/CheckboxToggle/CheckboxToggle'
import CloseButton from '~/components/atoms/CloseButton/CloseButton'
import Container from '~/components/atoms/Container/Container'
import { Icon } from '~/components/atoms/Icon/Icon'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import { getRouterPath } from '~/utils/getRouterPath'
import { useScrollPosition } from '~/utils/useScrollPosition'

import type { ArtistItem } from '../ArtistsHeadCatalogue/ArtistsHeadCatalogue'
import ArtistsHeadCatalogue from '../ArtistsHeadCatalogue/ArtistsHeadCatalogue'
import SearchField from '../SearchField/SearchField'

import styles from './ArtistsHead.module.css'

export interface ArtistsHeadProps {
  isIndex?: boolean
  artistList?: ArtistItem[]
}

export default function ArtistsHead({ artistList, isIndex }: ArtistsHeadProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const router = useRouter()
  const t = useTranslations('common')
  const tNav = useTranslations('navigation')

  const hasInitialized = useRef(false)

  const [isSticky, setIsSticky] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isScrollingUp, setIsScrollingUp] = useState(false)
  const { setMainHeaderVisible, mainHeaderVisible } = useInPageNavContext()

  const [featuredArtists, setFeaturedArtists] = useState(true)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  useEffect(() => {
    if (!isSheetOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSheetOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSheetOpen])

  useEffect(() => {
    // Only run this logic once when the component mounts and router is ready
    if (!router.isReady || hasInitialized.current) return

    const featuredParam = router.query?.featured

    if (featuredParam === undefined) {
      // No query param exists, set it to true
      router.replace(
        {
          pathname: getRouterPath(router),
          query: { ...router.query, featured: 'true' },
        },
        undefined,
        { shallow: true }
      )
      setFeaturedArtists(true)
    } else {
      // Query param exists, use its value
      setFeaturedArtists(featuredParam === 'true')
    }

    hasInitialized.current = true
  }, [router.isReady, router.pathname])

  // Handle subsequent changes to the featured query param
  useEffect(() => {
    if (!hasInitialized.current) return

    const featuredParam = router.query?.featured
    if (featuredParam !== undefined) {
      setFeaturedArtists(featuredParam === 'true')
    }
  }, [router.query?.featured])

  useScrollPosition(
    ({ currPos }) => {
      const pos = isMobile ? -457.5 : -574
      const isStickyPos = currPos.y <= pos

      setIsSticky(isStickyPos)

      // Detect scroll direction
      if (currPos.y > lastScrollY) {
        setIsScrollingUp(false)
      } else {
        setIsScrollingUp(true)
      }

      setLastScrollY(currPos.y)
    },
    [lastScrollY]
  )

  const hasSearchTerm = Boolean(router.query?.searchTerm)

  const renderCheckboxToggle = () => {
    return (
      <CheckboxToggle
        name="tradingView"
        value={featuredArtists}
        right={isMobile}
        isDisabled={hasSearchTerm}
        checkedText={tNav('artworksTab.featuredArtists')}
        onChange={async () => {
          router.push(
            {
              pathname: getRouterPath(router),
              query: { featured: !featuredArtists },
            },
            undefined,
            { shallow: true }
          )

          await multiPostHogEventPusher([
            {
              eventType: `${'Featured artists'} toggle clicked`,
            },
          ])
        }}
      />
    )
  }

  const renderDesktopView = () => {
    return (
      <div
        className={classNames(
          styles.base,
          isSticky && styles.baseSticky,
          isSticky && mainHeaderVisible && styles.pushDown
        )}
      >
        <Container>
          <div className={styles.stickyContainer}>
            <Text
              className={styles.title}
              size={TextSizes.primaryTitleMed}
              uppercase
            >
              {t('browseByLastName')}
            </Text>

            <ArtistsHeadCatalogue artistList={artistList} />

            <div className={styles.searchBox}>
              <SearchField
                placeholder={t('searchArtistName')}
                filterPosition={null}
              />
              {renderCheckboxToggle()}
            </div>
          </div>
        </Container>
      </div>
    )
  }

  const renderMobileView = () => {
    return (
      <>
        <div className={classNames(styles.mobileBase)}>
          <Container>
            <div className={styles.titleContainer}>
              <Text
                className={styles.title}
                size={TextSizes.primaryTitleMed}
                uppercase
              >
                {t('browseByLastName')}
              </Text>
            </div>
          </Container>
        </div>

        <div
          className={classNames(
            styles.mobileJumpToLetterBar,
            isSticky && mainHeaderVisible && styles.pushDown,
            isSticky && styles.shadow
          )}
        >
          <Container>
            <button
              type="button"
              className={styles.mobileJumpToLetterButton}
              onClick={() => setIsSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={isSheetOpen}
            >
              <Text size={TextSizes.bodyMed}>{t('jumpToLetter')}</Text>
              <span className={styles.mobileJumpToLetterChevron}>
                <Icon type="Chevron" />
              </span>
            </button>
          </Container>
        </div>
        <div
          className={classNames(
            styles.mobileSearchField,
            isSticky && mainHeaderVisible && styles.pushDown,
            !isScrollingUp && isSticky && styles.shadow
          )}
        >
          <Container>
            <div className={styles.searchBox}>
              <SearchField
                placeholder={t('searchArtworkTitle')}
                filterPosition={null}
              />
              {renderCheckboxToggle()}
            </div>
          </Container>
        </div>

        {isSheetOpen && (
          <div
            className={styles.sheetBackdrop}
            onClick={() => setIsSheetOpen(false)}
            role="presentation"
          >
            <div
              className={styles.sheet}
              role="dialog"
              aria-modal="true"
              aria-label={t('browseByLastName')}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.sheetHeader}>
                <Text
                  size={TextSizes.primaryTitleMed}
                  uppercase
                  className={styles.sheetTitle}
                >
                  {t('browseByLastName')}
                </Text>
                <CloseButton
                  onClick={() => setIsSheetOpen(false)}
                  ariaLabel={t('close')}
                />
              </div>
              <div className={styles.sheetBody}>
                <ArtistsHeadCatalogue
                  artistList={artistList}
                  onLetterClick={() => setIsSheetOpen(false)}
                />
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  return <>{isMobile ? renderMobileView() : renderDesktopView()}</>
}
